import { useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Card } from "../Card";
import { formatTimestamp } from "./utils";
import { registerChartComponents } from "../../utils/chartUtils";

const BuildingsWithFlatReadings = ({ flatReadings, viewMode, setViewMode }) => {
  const { t } = useTranslation();
  // Sort buildings by number of occurrences
  const sortedBuildings = useMemo(
    () =>
      [...flatReadings].sort(
        (a, b) => b.occurrences.length - a.occurrences.length
      ),
    [flatReadings]
  );

  useEffect(() => {
    registerChartComponents();
  }, []);

  const chartData = useMemo(() => {
    if (sortedBuildings.length === 0) {
      return { labels: [], datasets: [] };
    }
    return {
      labels: sortedBuildings.map((b) => b.buildingName),
      datasets: [
        {
          label: t("admin_chart_occurrences_label"),
          data: sortedBuildings.map((b) => b.occurrences.length),
          backgroundColor: "rgba(202, 183, 120, 0.6)",
          borderColor: "rgb(181, 166, 102)",
          borderWidth: 1,
        },
      ],
    };
  }, [sortedBuildings, t]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          beginAtZero: true,
          min: 0,
          ticks: { precision: 0 },
          title: {
            display: true,
            text: t("admin_chart_occurrences_label"),
          },
        },
        x: {
          title: {
            display: true,
            text: t("admin_chart_building_axis"),
          },
        },
      },
      plugins: {
        legend: { position: "top" },
        title: {
          display: true,
          text: t("admin_chart_title"),
        },
      },
    }),
    [t]
  );

  return (
    <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {t("admin_buildings_title")}
        </h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === "table"
                ? "bg-[#cabb78] text-gray-900"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {t("admin_table_view")}
          </button>
          <button
            onClick={() => setViewMode("chart")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === "chart"
                ? "bg-[#cabb78] text-gray-900"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {t("admin_chart_view")}
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
        <div className="overflow-hidden rounded-xl border border-gray-200">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_building_name")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_number_of_occurrences")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_latest_reading")}
                </th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t("admin_latest_timestamp")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedBuildings.map((building) => {
                const latestOccurrence = building.occurrences[0];
                return (
                  <tr
                    key={building.guid}
                    className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                  >
                    <td className="px-5 py-3 font-medium text-gray-900">
                      {building.buildingName}
                    </td>
                    <td className="px-5 py-3 text-gray-700 tabular-nums">
                      {building.occurrences.length}
                    </td>
                    <td className="px-5 py-3 text-gray-700 tabular-nums">
                      {latestOccurrence?.reading ?? "N/A"}
                    </td>
                    <td className="px-5 py-3 text-sm text-gray-600">
                      {latestOccurrence?.date
                        ? formatTimestamp(latestOccurrence.date)
                        : "N/A"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="h-96 rounded-xl border border-gray-200 bg-gray-50/50 p-4">
          {chartData.labels.length > 0 ? (
            <Bar data={chartData} options={chartOptions} />
          ) : (
            <p className="text-gray-500 text-sm">{t("admin_no_chart_data")}</p>
          )}
        </div>
      )}
    </Card>
  );
};

BuildingsWithFlatReadings.propTypes = {
  flatReadings: PropTypes.arrayOf(
    PropTypes.shape({
      buildingName: PropTypes.string.isRequired,
      guid: PropTypes.string.isRequired,
      occurrences: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string.isRequired,
          reading: PropTypes.number.isRequired,
        })
      ).isRequired,
    })
  ).isRequired,
  viewMode: PropTypes.oneOf(["table", "chart"]).isRequired,
  setViewMode: PropTypes.func.isRequired,
};

export default BuildingsWithFlatReadings;
